import React from "react";
import { Controller, FieldValues, useFormState, get } from "react-hook-form";
import { CommonFieldProps } from "@components/Form/index";

interface TextFieldProps<T extends FieldValues> extends CommonFieldProps<T> {
    type?: "text" | "email" | "password" | "tel";
    autoComplete?: string;
    inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
    showValidatedIcon?: boolean;
}

export function TextField<T extends FieldValues>(props: TextFieldProps<T>) {
    const {
        control,
        name,
        label,
        description,
        type = "text",
        disabled,
        error: externalError,
        showValidatedIcon = true,
        autoComplete,
        inputMode,
        className
    } = props;

    const { dirtyFields, errors } = useFormState({ control });

    return (
        <div className={`form__field ${className ?? ""}`}>
            <div className="form__field__input">
                <Controller
                    control={control}
                    name={name}
                    render={({ field, fieldState }) => {
                        const value = String(field.value ?? "");
                        const hasValue = value.length > 0;

                        const isDirty = !!get(dirtyFields, name);
                        const fieldError = fieldState.error?.message;
                        const isInvalid = !!fieldError || !!externalError;

                        const isFieldValidating = fieldState.isValidating;

                        const validated =
                            showValidatedIcon &&
                            !isInvalid &&
                            !isFieldValidating &&
                            isDirty &&
                            hasValue;

                        return (
                            <div
                                className={[
                                    "ds-input",
                                    disabled ? "ds-input--disabled" : "",
                                    hasValue ? "ds-input--filled" : "",
                                    validated ? "ds-input--validated" : "",
                                    isInvalid ? "ds-input--error" : "",
                                    isFieldValidating ? "ds-input--loading" : "",
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                            >
                                <label className="ds-input__label" htmlFor={field.name}>
                                    {label}
                                </label>

                                <input
                                    {...field}
                                    id={field.name}
                                    value={value}
                                    type={type}
                                    className="ds-input__control"
                                    disabled={disabled}
                                    autoComplete={autoComplete}
                                    inputMode={inputMode}
                                />

                                {isFieldValidating ?
                                    <span className="ds-input__icon validating" aria-hidden>
                                        …
                                    </span>
                                : validated ?
                                    <span className="ds-input__icon valid" aria-hidden>
                                        ✓
                                    </span>
                                : null}
                            </div>
                        );
                    }}
                />
            </div>

            {!!description &&
                <div className="form__field__description">
                    <p className={"tp-2xs"}>{description}</p>
                </div>
            }

            {(externalError || get(errors, name)) &&
                <div className="form__field__error">
                    {externalError || get(errors, name)?.message}
                </div>
            }
        </div>
    );
}